import { CommerceCartData } from "../../commerce/commerce.model";
import { AuditMetadata, Location, SearchModel } from "../../core/core";
import { TransportationInfo, TransportationEstimate, JobsiteContact } from "../../transportation/transportation";
import { Charge } from "../cart";
import { RelatedItem } from "./related-item";
import { Attachment, InventoryItemSummary } from "../../inventory/inventory";

export class Cart {
  cartKind?: "Equipment" | "Shop";
  commerce?: CommerceCartData;
  dailySummary: CartSummary;
  id: string = "";
  affiliateId?: string;
  originalCartId?: string;
  affiliateViewingCart: boolean; // front end only. if the person logged in is
  accountId: string;
  userId: string;
  rentalTotal: number;
  activeStatus: string;
  preventativeMaintenanceTotal: number;
  grandTotal: number;
  cartSummary: CartSummary;
  monthlySummary: CartSummary;
  items: CartItem[] = [];
  transportationInfo: TransportationInfo;
  paymentContacts: JobsiteContact[];
  formattedDuration: string;
  auditMetadata: AuditMetadata;
  isActive: boolean;
  emptyAttachments: RelatedItem[];
  poNumber: string;
  partitionKey: string;
  customerMetadata: CustomerMetadata;
  metadata: { [key: string]: string };
  jobType: string;
  deliveryOption: string;
  couponId?: string;
  couponCode?: string;
  shareInfo?: CartShareInfo;

  static monthlyTotal(monthlySummary: CartSummary) {
    if (!monthlySummary) {
      return 0;
    }

    return Object.values(monthlySummary).reduce((a, b) => a + b);
  }

  static monthlySubTotal(monthlySummary: CartSummary) {
    if (!monthlySummary) {
      return 0;
    }

    let monthlySubTotal = 0;
    for (const charge of Object.keys(monthlySummary)) {
      if (charge === "Tax") {
        continue;
      }
      monthlySubTotal += monthlySummary[charge];
    }
    return monthlySubTotal;
  }
}

export class CustomerMetadata {
  phoneNumber: string;
  ipAddress: string;
}

/**
 * Share-by-SMS bookkeeping the orders API keeps on the cart: the numbers it was texted to plus the
 * view counter it increments whenever a recipient opens the shared link.
 */
export class CartShareInfo {
  lastSharedToPhoneNumber?: string;
  sharedToPhoneNumbers?: string[];
  firstSharedDateTime?: string;
  lastSharedDateTime?: string;
  shareCount = 0;
  viewCount = 0;
  firstViewedDateTime?: string;
  lastViewedDateTime?: string;
  views?: CartShareView[];
  shares?: CartShare[];
}

export class CartShare {
  shortCode: string;
  channel?: string;
  recipientPhoneNumber: string;
  recipientEmail?: string;
  sharedDateTime: string;
  viewCount = 0;
  firstViewedDateTime?: string;
  lastViewedDateTime?: string;
  sendReadReceipt = false;
  senderPhoneNumber?: string;
  readReceiptSentDateTime?: string;
}

export class CartShareView {
  shortCode?: string;
  viewedDateTime: string;
  ipAddress?: string;
  userAgent?: string;
}

/** Shape returned when the Website resolves a compact `/s/{shortCode}` URL. */
export interface CartShareResolution {
  cartId: string;
  viewCount: number;
  firstViewedDateTime?: string;
  lastViewedDateTime?: string;
}

/** Shape of `POST /api/orders/v2/cart/{id}/share-sms`. */
export interface CartShareResult {
  shareUrl: string;
  shareCount: number;
  viewCount: number;
}

export class CartSummary {
  Rent?: number;
  Maintenance?: number;
  Attachment?: number;
  PremiumServices?: number;
  Tax?: number;
}

export class CartItem {
  id: string;
  status: string;
  accountId: string;
  title: string;
  subtitle: string;
  inventoryItem: InventoryItemSummary;
  imageThumbnail: string;
  beginDate: string;
  endDate: string;
  jobDuration: number;
  duration: number; // number of days
  formattedDuration: string;
  descriptors: string[];
  rentSubtotal: number;
  maintenanceSubtotal: number;
  total: number;
  subtotal: number;
  dailyRentalRate: number;
  weeklyRentalRate?: number;
  weeklyMultiplier?: number;
  charges: Charge[];
  chargeSummary: CartSummary; // totals for the WHOLE duration
  monthlySummary: CartSummary;
  weeklySummary: CartSummary;
  dailySummary: CartSummary;
  relatedItemTitle: string;
  attachments: Attachment[];
  transportationEstimateSummary?: TransportationEstimate;
  durationUnit: "day" | "week" | "month"; // client generated
  durationTotal: number; // client generated
  rpoInterested: boolean;
  renterWillTransport: boolean; // true if renter will haul the equipment themselves
  jobZipCode: string;
  jobLocation: Location;
  checkoutWarnings: string[]; // front end only

  constructor(beginDate: Date, durationInDays: number, item: any, attachments: any[], rpoInterested, jobZipCode?: string, imageThumbnail?: string) {
    // Coerce to number: durationSelected historically emitted strings, and
    // `date + "15" - 1` string-concats into a garbage end date.
    const days = Number(durationInDays) || 0;
    this.beginDate = beginDate.toDateString();
    this.inventoryItem = new InventoryItemSummary(item.id, item.catalogItemId, item.accountId, item?.weight || 0);
    this.duration = days;
    this.formattedDuration = CartItem.formatDuration(days);
    this.rpoInterested = rpoInterested;
    this.jobZipCode = jobZipCode;
    this.imageThumbnail = imageThumbnail;

    // Duration is inclusive; last on-rent day is begin + duration - 1.
    if (days > 0) {
      const end = new Date(beginDate);
      end.setDate(end.getDate() + days - 1);
      this.endDate = end.toDateString();
    }

    //formulate formatted duration here
    this.attachments = attachments;
  }

  static getEndDate(beginDate: Date, jobDuration: number): string {
    return new Date(beginDate.setMonth(beginDate.getMonth() + jobDuration)).toDateString();
  }

  /**
   * Human-readable rental duration derived from an exact day count. Mirrors
   * billing rollup: remainder of 3+ days on a weekly rental reads as another
   * week (e.g. 10 days → "2 weeks"); remainder of 21+ days on a monthly
   * rental reads as another month. Smaller remainders show as days.
   */
  static formatDuration(days: number): string {
    if (!days || days <= 0) {
      return "";
    }

    if (days < 21) {
      let weeks = Math.floor(days / 7);
      let remainingDays = days % 7;
      if (remainingDays >= 3) {
        weeks += 1;
        remainingDays = 0;
      }
      const parts: string[] = [];
      if (weeks > 0) {
        parts.push(`${weeks} ${weeks === 1 ? "week" : "weeks"}`);
      }
      if (remainingDays > 0) {
        parts.push(`${remainingDays} ${remainingDays === 1 ? "day" : "days"}`);
      }
      return parts.join(" and ");
    }

    let months = Math.floor(days / 28);
    let remainingDays = days % 28;
    if (remainingDays >= 21) {
      months += 1;
      remainingDays = 0;
    }
    const parts: string[] = [];
    if (months > 0) {
      parts.push(`${months} ${months === 1 ? "month" : "months"}`);
    }
    if (remainingDays > 0) {
      parts.push(`${remainingDays} ${remainingDays === 1 ? "day" : "days"}`);
    }
    return parts.join(" and ");
  }
}

export class CartSearchModel extends SearchModel {
  cartKind?: "Equipment" | "Shop";
  abandonedCheckout?: boolean;
  userId: string;
  active: boolean = true;
  createdFrom: string;
  createdTo: string;
  skipCartRebuild: boolean;
}

export const DeliveryOptions = {
  Delivery: "Delivery",
  PickUp: "PickUp",
};
